import {
  createApiToken,
  destroyApiToken,
  getApiToken,
} from "@/api/endpoints/api-token";
import { API_TOKEN_ERROR } from "@/constants";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/ui/alert-dialog";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@components/ui/empty";
import ErrorComponent from "@components/ui/error";
import { InlineCode } from "@components/ui/inline-code";
import { Skeleton } from "@components/ui/skeleton";
import { Spinner } from "@components/ui/spinner";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery } from "@tanstack/react-query";
import { KeyIcon, PlusIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

export default function ApiKey() {
  const { t } = useLingui();

  // URL state
  const { orgUuid } = useParams<{ orgUuid: string }>();

  // Server state
  const {
    data: apiTokenData,
    error: apiTokenError,
    refetch: refetchApiToken,
    isLoading: isApiTokenLoading,
  } = useQuery({
    queryKey: [orgUuid, "api-token"],
    queryFn: getApiToken,
    enabled: !!orgUuid,
  });

  const { mutate: mutateCreateApiToken, isPending: isCreatingApiToken } =
    useMutation({
      mutationFn: createApiToken,
      onSuccess: (res) => {
        if (!res.ok) {
          throw new Error();
        }

        toast.success(t`API-nyckel skapad`);
        refetchApiToken();
      },
      onError: () => {
        toast.error(t`Misslyckades att skapa API-nyckel`);
      },
    });

  const { mutate: mutateDeleteApiToken, isPending: isDeletingApiToken } =
    useMutation({
      mutationFn: destroyApiToken,
      onSuccess: (res) => {
        if (!res.ok) {
          throw new Error();
        }

        toast.success(t`API-nyckel raderad`);
        refetchApiToken();
      },
      onError: () => {
        toast.error(t`Misslyckades att radera API-nyckel`);
      },
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">
          <Trans>API-nyckel</Trans>
        </CardTitle>
        <CardDescription>
          Här är din API-nyckel. Du kan använda den för att autentisera mot All
          Ears API.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {apiTokenData ? (
          apiTokenData.ok ||
          apiTokenData.data?.error === API_TOKEN_ERROR.NO_TOKEN_FOUND ? (
            apiTokenData.ok && apiTokenData.data.token ? (
              <>
                <InlineCode className="w-fit">
                  {apiTokenData.data.token}
                </InlineCode>

                <AlertDialog>
                  <AlertDialogTrigger
                    render={
                      <Button
                        type="button"
                        variant="destructive"
                        className="w-fit"
                      />
                    }
                  >
                    <Trans>Radera API-nyckel...</Trans>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        <Trans>Är du säker?</Trans>
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        <Trans>
                          Den här åtgärden kan inte ångras. API-nyckeln kommer
                          att raderas permanent och eventuella integrationer som
                          använder den kommer att sluta fungera.
                        </Trans>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        <Trans>Avbryt</Trans>
                      </AlertDialogCancel>
                      <AlertDialogAction
                        variant="destructive"
                        onClick={() => mutateDeleteApiToken()}
                        disabled={isDeletingApiToken}
                      >
                        <Trans>Radera</Trans>
                        {isDeletingApiToken && <Spinner />}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            ) : (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <KeyIcon />
                  </EmptyMedia>
                  <EmptyTitle>
                    <Trans>Ingen nyckel skapad</Trans>
                  </EmptyTitle>
                  <EmptyDescription>
                    <Trans>Skapa en nyckel för att börja använda API:t.</Trans>
                  </EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Button
                    type="button"
                    onClick={() => mutateCreateApiToken()}
                    disabled={isCreatingApiToken}
                  >
                    <Trans>Skapa API-nyckel</Trans>
                    {isCreatingApiToken ? <Spinner /> : <PlusIcon />}
                  </Button>
                </EmptyContent>
              </Empty>
            )
          ) : (
            <ErrorComponent
              heading={t`Åh nej!`}
              description={t`Misslyckades att hämta API-nyckeln. Vänligen försök igen senare.`}
            />
          )
        ) : isApiTokenLoading ? (
          <>
            <Skeleton className="h-7 w-96" />

            <Skeleton className="w-40 h-9" />
          </>
        ) : apiTokenError ? (
          <ErrorComponent
            heading={t`Åh nej!`}
            description={t`Misslyckades att hämta API-nyckeln. Vänligen försök igen senare.`}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
